import { useCallback, useContext, useEffect, useMemo } from 'react';
import { fetchCoupons, postCoupon } from 'src/libs/api/couponApi';
import { CouponHistory } from 'src/models/coupon/CouponHistory';
import { AuthContext } from 'src/stores/Auth';
import { BrowserCacheContext } from 'src/stores/BrowserCache';
import { I18nContext } from 'src/stores/I18n';
import useSWR, { KeyedMutator } from 'swr';
import { useModal } from './useModal';
import { useSharedState } from './useSharedState';

const useCoupon = (): {
  coupons?: CouponHistory[];
  isLoading: boolean;
  error: Error;
  mutate: KeyedMutator<CouponHistory[]>;
  addCoupon: (promoCode: string) => Promise<void>;
  changeCoupon: (coupon: CouponHistory) => void;
  deleteCoupon: () => void;
  currentCoupon?: CouponHistory;
} => {
  const { user } = useContext(AuthContext);
  const { setIsVisible, setModalParams } = useModal('noticeModal');
  const localeResources = useContext(I18nContext);
  const { couponHistoryId } = useContext(BrowserCacheContext);
  const [currentCoupon, setCurrentCoupon] = useSharedState<CouponHistory | undefined>('currentCoupon', undefined);
  const fetcher = useCallback(async () => {
    const res = await fetchCoupons();
    if (res.isLeft()) {
      throw new Error(res.value.errors.join(','));
    }
    return res.value;
  }, []);
  const { data, error, isLoading, mutate } = useSWR(user ? `/api/coupons` : null, fetcher, {
    errorRetryCount: 0,
  });
  const addCoupon = async (promoCode: string) => {
    const response = await postCoupon(promoCode);
    if (response.isLeft()) {
      setModalParams({
        type: 'warning',
        title: localeResources.commons.errorOccurred,
        text: response.value.errors[0],
      });
      setIsVisible(true);
      return;
    }
    if (data?.length) {
      mutate([...data, response.value]);
    } else {
      mutate([response.value]);
    }
  };
  const changeCoupon = (coupon: CouponHistory) => {
    setCurrentCoupon(coupon);
    couponHistoryId.set(coupon.coupon.couponHistoryId);
  };
  const deleteCoupon = () => {
    setCurrentCoupon(undefined);
    couponHistoryId.delete();
    return;
  };
  const defaultSelectedCoupon = useMemo(() => {
    if (!data) return;
    const selected = data.find((coupon) => coupon.coupon.couponHistoryId === couponHistoryId.get());
    // couponIdがStorageに存在するのにselectedがない場合、そのクーポンは無効だとみなしてStorageから削除する
    if (couponHistoryId.get() && !selected) {
      couponHistoryId.delete();
    }
    return selected;
  }, [couponHistoryId, data]);

  useEffect(() => {
    if (defaultSelectedCoupon) {
      setCurrentCoupon(defaultSelectedCoupon);
    }
  }, [defaultSelectedCoupon]);

  return {
    coupons: data,
    isLoading,
    error,
    mutate,
    addCoupon,
    changeCoupon,
    deleteCoupon,
    currentCoupon,
  };
};

export { useCoupon };
